/**
 * Buddy Group Invitation Service — API functions for invitation endpoints.
 *
 * All invitation operations route through /api/buddy-group-ops workflow
 * endpoints which enforce authentication, authorization, and business rules.
 *
 * Endpoint map:
 *   POST /buddy-group-ops/invite                    -> sendInvitation
 *   GET  /buddy-group-ops/invitations/pending        -> getPendingInvitations
 *   POST /buddy-group-ops/invitations/:id/accept     -> acceptInvitation
 *   POST /buddy-group-ops/invitations/:id/decline    -> declineInvitation
 */

import { httpClient } from '../api/http-client';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Raw invitation row from backend GET /invitations/pending */
interface RawPendingInvitation {
  id: string;
  buddy_group_id: string;
  inviter_user_profile_id: string;
  invitee_user_profile_id: string;
  status: string;
  expires_at: string;
  created_at: string | null;
  buddy_groups?: {
    id?: string;
    group_name?: string | null;
  } | null;
  inviter?: {
    id: string;
    display_name: string | null;
  } | null;
  members?: Array<{
    user_profile_id: string;
    display_name: string | null;
    email: string | null;
    is_primary: boolean;
  }> | null;
}

/** A buddy group member as seen from a pending-invitation card */
export interface PendingInvitationMember {
  userProfileId: string;
  displayName: string | null;
  email: string | null;
  isPrimary: boolean;
}

/** Flattened pending invitation for frontend consumption */
export interface PendingInvitation {
  invitationId: string;
  buddyGroupId: string;
  groupName: string | null;
  invitedById: string;
  invitedByName: string | null;
  createdAt: string;
  members: PendingInvitationMember[];
}

/** Response from POST /invite */
interface InviteResponse {
  invitation: {
    id: string;
    buddy_group_id: string;
    inviter_user_profile_id: string;
    invitee_user_profile_id: string;
    status: string;
    expires_at: string;
    created_at: string | null;
  };
}

/** Response from POST /invitations/:id/accept or /decline */
interface AcceptDeclineResponse {
  success: boolean;
  buddy_group_id?: string;
}

// ─── Service Functions ────────────────────────────────────────────────────────

/**
 * Send a buddy group invitation to a user.
 * The backend enforces: duplicate detection, group membership checks,
 * group capacity limits, and that the inviter is a group member.
 */
export async function sendInvitation(
  buddyGroupId: string,
  inviteeProfileId: string
): Promise<{ invitation_id: string }> {
  const res = await httpClient.post<InviteResponse>(
    '/buddy-group-ops/invite',
    {
      buddy_group_id: buddyGroupId,
      invitee_user_profile_id: inviteeProfileId,
    }
  );
  return { invitation_id: res.invitation.id };
}

/**
 * Get all pending invitations for the current authenticated user.
 * Backend infers user from session cookie — no user param needed.
 *
 * Maps the raw backend response (with nested buddy_groups) to a
 * flat PendingInvitation interface for UI consumption.
 */
export async function getPendingInvitations(): Promise<PendingInvitation[]> {
  const res = await httpClient.get<{
    invitations: RawPendingInvitation[];
  }>('/buddy-group-ops/invitations/pending');

  return (res?.invitations ?? []).map((inv) => ({
    invitationId: inv.id,
    buddyGroupId: inv.buddy_group_id,
    groupName: inv.buddy_groups?.group_name ?? null,
    invitedById: inv.inviter_user_profile_id,
    invitedByName: inv.inviter?.display_name ?? null,
    createdAt: inv.created_at ?? new Date().toISOString(),
    members: (inv.members ?? []).map((m) => ({
      userProfileId: m.user_profile_id,
      displayName: m.display_name,
      email: m.email ?? null,
      isPrimary: m.is_primary,
    })),
  }));
}

/**
 * Accept a pending buddy group invitation.
 * Adds the current user to the group and marks the invitation as accepted.
 */
export async function acceptInvitation(
  invitationId: string
): Promise<void> {
  await httpClient.post<AcceptDeclineResponse>(
    `/buddy-group-ops/invitations/${invitationId}/accept`
  );
}

/**
 * Decline a pending buddy group invitation.
 * Marks the invitation as declined without adding the user to the group.
 */
export async function declineInvitation(
  invitationId: string
): Promise<void> {
  await httpClient.post<AcceptDeclineResponse>(
    `/buddy-group-ops/invitations/${invitationId}/decline`
  );
}

/**
 * Cancel/revoke a pending invitation via the workflow endpoint.
 * Used by the primary cardholder to remove a pending invitation from the group.
 * The backend cancels the invitation and notifies the invitee.
 */
export async function cancelInvitation(invitationId: string): Promise<void> {
  await httpClient.post(`/buddy-group-ops/invitations/${invitationId}/cancel`);
}
